<template>
    <div v-if="hasActiveGroups" class="prompt-guidance">

        <!--<span class="label">
			Additional Guidance
		</span>-->

        <div v-if="allSelected.length" class="selection">
            <ChipBase 
                v-for="selection in allSelected" 
                :key="selection.groupTitle + '-' + selection.item" 
                :color="selection.groupColor"
                :group="capitalize(selection.groupTitle)"
                :label="capitalize(selection.item)"
                @click="remove(selection)" 
                @close="remove(selection)" />
        </div>

        <v-menu 
            v-model="menu" 
            location="center center" 
            transition="slide-y-transition">

            <template #activator="{ props }">
                <v-btn variant="text" v-bind="props">
                    <v-icon icon="fa-solid fa-plus" />
                    <span>Add<!--<span class="hide-on-tiny">&nbsp;Style / Pose / View</span>--></span>
                </v-btn>
            </template>

            <v-card class="prompt-guidance-card">
                <div v-for="group in activeGroups" :key="group.title" class="group">

                    <div class="label">
                        <v-icon :icon="group.icon" />
                        <span>{{ group.title }}</span>
                    </div>

                    <v-list 
                        :multiple="group.multiple" 
                        return-object 
                        class="list"
                        density="compact" 
                        v-model:selected="selected[group.title]" 
                        :color="group.color"
                        :select-strategy="group.multiple ? 'classic' : 'single-leaf'">
                        <v-list-item v-for="item in group.items" 
                            :key="item" 
                            :title="capitalize(item)" 
                            :value="item" />
                    </v-list>

                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import AUGMENTATION from '@/constants/augmentation.js'
import ChipBase from '@/components/common/chip/ChipBase.vue'

export default {
    name: 'PromptGuidance',
    components: { ChipBase },
    props: {
        prompt: {
            type: Object,
            default: null
        },
        custom: {
            type: Object,
            default: null
        },
        active: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            menu: false,
            groups: [
                {
                    title: 'styles',
                    icon: 'fa-solid fa-palette',
                    color: 'color1',
                    multiple: false,
                    items: [
                        AUGMENTATION.STYLE.PHOTOREALISTIC,
                        AUGMENTATION.STYLE.LOW_POLY,
                        AUGMENTATION.STYLE.RENDER,
                        AUGMENTATION.STYLE.TOON,
                    ]
                },
                {
                    title: 'poses',
                    icon: 'fa-solid fa-person',
                    color: 'color2',
                    multiple: false,
                    items: [
                        AUGMENTATION.POSE.APOSE,
                        AUGMENTATION.POSE.TPOSE,
                        AUGMENTATION.POSE.NEUTRAL,
                        AUGMENTATION.POSE.SITTING,
                        AUGMENTATION.POSE.WALKING
                    ]
                },
                {
                    title: 'views',
                    icon: 'fa-solid fa-video',
                    color: 'secondary',
                    multiple: false,
                    items: [
                        AUGMENTATION.VIEW.FRONT,
                        AUGMENTATION.VIEW.BACK,
                        AUGMENTATION.VIEW.LEFT,
                        AUGMENTATION.VIEW.RIGHT,
                        AUGMENTATION.VIEW.DIAGONAL
                    ]
                }
            ],
            selected: {}
        }
    },
    created() {
        this.groups.forEach(group => {
            if (!this.selected[group.title]) this.$set
                ? this.$set(this.selected, group.title, [])
                : (this.selected[group.title] = [])
        })
    },
    computed: {
        hasActiveGroups() {
            return this.activeGroups.length > 0
        },
        activeGroups() {
            return this.groups.filter(group => this.active.includes(group.title))
        },
        allSelected() {
            return Object.entries(this.selected).flatMap(([groupTitle, value]) => {
                const group = this.activeGroups.find(g => g.title === groupTitle)
                const arr = Array.isArray(value) ? value : (value ? [value] : [])
                return arr.map(val => ({
                    item: val,
                    groupTitle,
                    groupColor: group?.color || null
                }))
            })
        },
        promptUser() {
            return this.prompt?.user || ''
        },
        customAugmentation() {
            return this.custom?.augmentation || {}
        }
    },
    watch: {
        selected: {
            handler(newValue, oldValue) {
                if (newValue === oldValue) return
                this.custom.augmentation = this.selected
            },
            deep: true
        },
        customAugmentation(newValue, oldValue) {
            if (newValue === oldValue) return
            this.selected = newValue
        }
    },
    methods: {
        remove(selection) {
            const current = this.selected[selection.groupTitle]
            const arr = Array.isArray(current) ? current : (current ? [current] : [])
            const filtered = arr.filter(i => i !== selection.item)
            this.selected[selection.groupTitle] = filtered
        },
        getCustomAugmentation() {
            return this.allSelected.map(selection => 
                `{${selection.groupTitle.toLowerCase()}:${selection.title.toLowerCase()}}`).join(' ')
        },
        capitalize(text) {
            if (!text) return ''
            return text.charAt(0).toUpperCase() + text.slice(1)
        }
    },
    mounted() {
        this.selected = this.customAugmentation
    }
}
</script>

<style>
.prompt-guidance {
    width: 100%;
    margin-top: 0.5rem;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
}

.prompt-guidance .label {
    font-size: 1.2rem;
    color: rgb(var(--v-theme-secondary));
}

.prompt-guidance .selection {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
}

.prompt-guidance-card {
    margin-top: 5px;
    padding: 10px;
    border-radius: 8px !important;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: 5px;
    background-color: rgb(var(--v-theme-darkbase)) !important;
}

.prompt-guidance-card .group {
    flex: 1;
    min-width: 150px;
}

.prompt-guidance-card .label {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 8px;
    margin-bottom: 4px;
}

.prompt-guidance-card .label .v-icon {
    font-size: 1rem;
}

.prompt-guidance-card .label span {
    font-weight: bold;
    margin-left: 10px;
    text-transform: capitalize;
}

.prompt-guidance-card .list {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

@media (max-width: 400px) {
    .hide-on-tiny {
        display: none;
    }

    .prompt-guidance {
        justify-content: space-between;
    }
}
</style>
